import React from 'react';
import { observer } from 'mobx-react';
import { autorun, computed, observable } from 'mobx';
import { AuthenticateStore, ServerStore } from '@seedlang/stores';
import { AuthenticateStatus } from 'components/authenticate/authenticate_status';
import { AppUI, TreeUI } from '@seedlang/state';
import FormWrapper from 'components/authenticate/form_wrapper';
import Button from 'components/button/button';
import { getCookieValue, isBlank } from '@seedlang/utils';
import { Theme } from '@seedlang/constants';
import Text from 'components/text';
import styled from '@emotion/styled';
import AppleSignInButton from './apple_sign_in_button';

const Subtitle = styled.div`
  font-size: 14px;
  margin: -5px 0 10px 0;
  font-weight: bold;
  text-align: center;
`;

export const AuthenticateSignin = ({
  accentColor,
  afterSignIn,
  autoFocus,
  disabled,
  email,
  errorMessage,
  onChangeEmail,
  onChangePassword,
  onSubmit,
  password,
  published,
  signInHeader,
  siteIsSeedlang,
  successMessage,
  teamName,
  transparentAccentColor,
}) => (
  <FormWrapper
    padding={0}
    color={siteIsSeedlang ? '#333' : '#000'}
  >
    <form
      acceptCharset='UTF-8'
      onSubmit={onSubmit}
      id='new_user'
    >
      <Text
        heading='3'
        center
        margin='0 0 10px 0'
        color='#333'
      >
        {signInHeader}
      </Text>
      {
        published && !siteIsSeedlang &&
          <Subtitle>
            {`Sign in with Your ${teamName} or Seedlang Credentials`}
          </Subtitle>
      }
      {
        !published && !siteIsSeedlang &&
          <Subtitle>
            Sign in With Your Early Access Credentials
          </Subtitle>
      }
      <AuthenticateStatus
        errorMessage={errorMessage}
        successMessage={successMessage}
      />
      <div>
        <label htmlFor='user_email'>Email</label>
        <input
          required
          autoFocus={autoFocus}
          type='email'
          value={email}
          onChange={el => onChangeEmail(el.target.value)}
        />
      </div>
      <div>
        <label htmlFor='user_password'>Password</label>
        <input
          required
          type='password'
          value={password}
          onChange={el => onChangePassword(el.target.value)}
        />
      </div>
      <Button
        margin='10px 0 0 0'
        width='100%'
        disabled={disabled}
        onClick={onSubmit}
        height='50px'
        borderRadius='50px'
        scaleOnHover={disabled ? 1 : 1.05}
        background={siteIsSeedlang || isBlank(accentColor) ? Theme.blue : accentColor}
        backgroundOnHover={siteIsSeedlang || isBlank(accentColor) ? Theme.blue : transparentAccentColor}
        backgroundOnActive={siteIsSeedlang || isBlank(accentColor) ? Theme.blue : accentColor}
        color={disabled ? '#e4e4e4' : '#FFF'}
      >
        Sign In
      </Button>

      {
        siteIsSeedlang && (
          <div style={{ margin: '5px 0 0 0' }}>
            <AppleSignInButton afterSignIn={afterSignIn} scaleOnHover={1.05} />
          </div>
        )
      }
    </form>
  </FormWrapper>
);

@observer
class AuthenticateSigninWrapper extends React.Component {
  @observable email = '';
  @observable password = '';
  @observable readyForRedirect = false;

  @computed get disabled() {
    return isBlank(this.email) || isBlank(this.password);
  }

  @computed get signInHeader() {
    return AppUI.siteIsSeedlang ? `Sign In to ${AppUI.user?.site?.name}` : 'Sign In';
  }

  componentDidMount() {
    ServerStore.clearMessages();
    this.disposeAutorun = autorun(() => {
      if (this.readyForRedirect) {
        AppUI.routeToSignedInHome();
      }
    });
  }

  componentWillUnmount() {
    if (this.disposeAutorun) {
      this.disposeAutorun();
    }
  }
  onSubmit = (e) => {
    e.preventDefault();
    if (!this.disabled) {
      AuthenticateStore.signIn({
        data: {
          assign_membership_group_id: AppUI.getCookieValue('assign_membership_group_id'),
          concept_id: this.props.conceptId,
          password: this.password,
          email: this.email,
          early_access_token: getCookieValue('early-access-token'),
        },
        failureCallback: this.onSignInFailure,
      }, this.afterSignIn);
    }
  }
  afterSignIn = (resp) => {
    if (resp.success) {
      this.afterLoadUser(resp);
      this.readyForRedirect = true;
      if (AppUI.siteIsSeedlang) {
        AppUI.loadReviewDecks();
        TreeUI.loadTreesFromTargetLanguage(AppUI.targetLanguageId);
      }
      if (this.props.onboarding) {
        AppUI.createEvent('onboarding - finish onboarding');
      }
    } else {
      ServerStore.setErrorMessage(resp.message);
    }
  }
  onSignInFailure = (resp) => {
    // Note: errors are intercepted by Api.ajax, the error is stored in ServerStore and rendered in `<AuthenticateStatus/>`

    if (resp.code === 'unconfirmed') {
      this.props.onConfirmationRequired({
        email: this.email,
        action: 'signIn',
      });
    }
  }
  afterLoadUser = (resp) => {
    AppUI.setAuthUserStoreShowData(resp);
    if (this.props.onboarding) {
      this.readyForRedirect = true;
    }
  }

  render() {
    return (
      <AuthenticateSignin
        accentColor={AppUI.site.accentColor}
        afterSignIn={this.afterSignIn}
        autoFocus={!AppUI.layout.isMobile}
        disabled={this.disabled}
        email={this.email}
        errorMessage={ServerStore.errorMessage}
        onChangeEmail={email => this.email = email}
        onChangePassword={password => this.password = password}
        onSubmit={this.onSubmit}
        password={this.password}
        published={AppUI.site.published}
        signInHeader={this.signInHeader}
        siteIsSeedlang={AppUI.siteIsSeedlang}
        successMessage={ServerStore.successMessage}
        teamName={AppUI.user?.site?.teamName}
        transparentAccentColor={AppUI.site.transparentAccentColor}
      />
    );
  }
}

export default AuthenticateSigninWrapper;
